/**
 * LUDOPIA — la carte de connexion à la plateforme, dans les réglages.
 *
 * ══ Pourquoi elle vit à côté du compte, et pas à sa place ═══════════════════
 *
 * Ce sont **deux identités distinctes**, et l'écran doit le montrer plutôt que le cacher :
 *
 * · le compte Villopia (Supabase) possède la ville, le slug public, la sauvegarde ;
 * · le compte Ludopia porte un pseudo, un avatar et des Ludos qui traversent les jeux.
 *
 * Les présenter comme une seule chose ferait croire qu'en perdant l'un on perd l'autre —
 * ce qui est faux, et inquiétant au moment précis où l'on demande à quelqu'un de se
 * connecter.
 *
 * ══ Ce que cette carte ne fait JAMAIS ═══════════════════════════════════════
 *
 * ⚠️ **Aucun champ, aucun mot de passe.** C'est le premier principe du guide Ludopia, et
 * c'est aussi ce qui rend OAuth utile : le mot de passe du joueur ne traverse pas
 * Villopia. Un formulaire « pratique » ici annulerait toute la chaîne.
 *
 * La carte ne fait que trois choses : envoyer vers `ludopia.fr`, montrer qui est revenu,
 * et permettre de partir.
 *
 * ══ Ce qu'elle affiche quand elle ne sait pas ═══════════════════════════════
 *
 * Rien d'inventé. Un solde inconnu ne s'affiche pas à zéro — « 0 Ludo » et « je n'ai pas
 * pu lire ton solde » sont deux états différents, et les confondre est exactement le
 * défaut qui a coûté une matinée sur le tableau de bord.
 */

import { n, t } from '@/i18n';
import {
  ludopiaBourse,
  ludopiaConnexion,
  ludopiaDeconnexion,
  ludopiaJeton,
  ludopiaMoi,
} from '@/net/ludopia';
import type { LudopiaJoueur } from '@/net/ludopia';
import { createButton, createPanel } from './components';
import { icon } from './icons';

export interface LudopiaSectionHandle {
  readonly el: HTMLElement;
  /** Relit l'identité et le solde. Sans jeton, remet la carte à « déconnecté ». */
  refresh(): void;
}

export function createLudopiaSection(): LudopiaSectionHandle {
  const title = document.createElement('h3');
  title.className = 'ludopia__title';
  title.append(icon('gamepad'), t('ludopia.title'));

  const pitch = document.createElement('p');
  pitch.className = 'ludopia__pitch';
  pitch.textContent = t('ludopia.pitch');

  /** Ligne d'identité : avatar et pseudo, une fois connecté. */
  const identity = document.createElement('div');
  identity.className = 'ludopia__identity';
  identity.hidden = true;

  const avatar = document.createElement('img');
  avatar.className = 'ludopia__avatar';
  avatar.alt = '';
  avatar.hidden = true;

  const pseudo = document.createElement('b');
  pseudo.className = 'ludopia__pseudo';

  const purse = document.createElement('span');
  purse.className = 'ludopia__purse';

  identity.append(avatar, pseudo, purse);

  const connect = createButton({
    label: t('ludopia.connect'),
    variant: 'primary',
    icon: 'gamepad',
  });
  const disconnect = createButton({ label: t('ludopia.disconnect'), variant: 'ghost' });
  disconnect.el.hidden = true;

  const panel = createPanel({
    children: [title, pitch, identity, connect.el, disconnect.el],
  });
  panel.el.classList.add('ludopia');

  /** Passe la carte en mode « connecté » ou « déconnecté ». Un seul endroit qui décide. */
  const show = (joueur: LudopiaJoueur | null): void => {
    const linked = joueur !== null;
    identity.hidden = !linked;
    connect.el.hidden = linked;
    disconnect.el.hidden = !linked;
    pitch.hidden = linked;

    if (!linked) {
      purse.textContent = '';
      avatar.hidden = true;
      return;
    }

    pseudo.textContent = joueur.pseudo;
    if (joueur.avatar !== undefined && joueur.avatar !== '') {
      avatar.src = joueur.avatar;
      avatar.hidden = false;
    } else {
      avatar.hidden = true;
    }
  };

  function refresh(): void {
    if (ludopiaJeton() === null) {
      show(null);
      return;
    }

    void ludopiaMoi().then((joueur) => {
      show(joueur);
      if (joueur === null) return;

      /*
        Le solde est lu SÉPARÉMENT, et son absence n'efface pas l'identité. Un joueur
        reconnu dont la bourse ne répond pas reste un joueur reconnu ; le contraire —
        tout masquer parce qu'un chiffre manque — lui ferait croire qu'il est déconnecté.
      */
      void ludopiaBourse().then((bourse) => {
        purse.textContent = bourse === null ? '' : t('ludopia.purse', { value: n(bourse.solde) });
      });
    });
  }

  connect.el.addEventListener('click', () => {
    void ludopiaConnexion();
  });

  disconnect.el.addEventListener('click', () => {
    disconnect.el.setAttribute('disabled', '');
    void ludopiaDeconnexion().finally(() => {
      disconnect.el.removeAttribute('disabled');
      show(null);
    });
  });

  refresh();

  return { el: panel.el, refresh };
}
